'use client';

import React from 'react';

import NextImage, { type ImageProps } from 'next/image';

import { CMS_IMAGE_QUALITY, isOptimizableImageSrc } from '@/lib/images/cmsImage';

type CmsImageProps = Omit<ImageProps, 'src' | 'loader' | 'unoptimized' | 'quality'> & {
  src: string;
};

/**
 * `next/image` for publisher (CMS) assets.
 *
 * Images on our media hosts go through `/_next/image`, which resizes them to the
 * slot they render in and serves WebP. Any other host renders the original URL
 * unchanged, so third-party assets behave exactly as a plain `<img>`.
 *
 * Layout is unchanged from the `<img>` it replaces: `width`/`height` here only
 * describe the slot for `srcset` generation; the CSS classes still size the element.
 */
export const CmsImage = ({
  src,
  sizes,
  width,
  priority,
  fetchPriority,
  ...rest
}: CmsImageProps): React.ReactElement => (
  <NextImage
    src={src}
    width={width}
    sizes={sizes ?? defaultSizes(width)}
    quality={CMS_IMAGE_QUALITY}
    unoptimized={!isOptimizableImageSrc(src)}
    priority={priority}
    // `priority` alone only preloads and disables lazy-loading; the request itself
    // stays at the browser's default (low) image priority and queues behind fonts
    // and scripts. The one image a page is waiting on should not.
    fetchPriority={fetchPriority ?? (priority ? 'high' : undefined)}
    {...rest}
  />
);

/**
 * With `sizes` set, next/image emits a `w`-descriptor srcset over every configured
 * width, so 3x screens get a sharp candidate. Without it, fixed-width images only
 * get a 1x/2x pair and `width={0}` (unknown-dimension) images collapse to 16px.
 */
const defaultSizes = (width: CmsImageProps['width']): string | undefined => {
  if (typeof width !== 'number') return undefined;
  return width > 0 ? `${width}px` : '100vw';
};
